import { Component, ChangeDetectionStrategy, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { Button } from '@openng/optimus-ui/button';
import { Message } from '@openng/optimus-ui/message';
import { Password } from '@openng/optimus-ui/password';
import { ProgressSpinner } from '@openng/optimus-ui/progressspinner';

import { LogService } from '../../core/services/log.service';
import { SetPasswordService } from './services/set-password.service';

type Step = 'loading' | 'form' | 'invalid' | 'success';

// Mismo layout de dos columnas que AuthInitComponent (features/auth/auth-init.component.ts):
// panel izquierdo con el logo de producto (oculto en móvil), panel derecho con la tarjeta.
// Duplicado a propósito en vez de extraído a un componente compartido, para no tocar el
// login (que hoy funciona) al integrar esta pantalla — ver ITSM 1839224.
@Component({
    selector: 'app-set-password',
    standalone: true,
    imports: [CommonModule, FormsModule, Button, Message, Password, ProgressSpinner, TranslatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <div class="min-h-screen flex items-center justify-center bg-[#3e2c42] py-12 px-4 sm:px-6 lg:px-8">
            <div class="max-w-3xl w-full flex bg-(--stela-surface) rounded-2xl shadow-2xl overflow-hidden">
                <!-- Left Column: product logo (hidden on mobile) -->
                <div class="hidden lg:flex shrink-0 w-72 bg-linear-to-br from-[#d1d1d1] to-[#c0c0c0] items-center justify-center p-8">
                    <img src="/assets/img/Logo_stela_blanco.png" class="w-full max-h-64 object-contain drop-shadow-lg" alt="Stela Logo">
                </div>

                <!-- Right Column: card -->
                <div class="flex-1 flex items-center justify-center p-8 lg:p-12">
                    <div class="w-full max-w-sm">
                        @switch (step()) {
                            @case ('loading') {
                                <div class="flex justify-center py-10">
                                    <p-progressSpinner />
                                </div>
                            }
                            @case ('invalid') {
                                <div class="text-center py-4">
                                    <i class="pi pi-exclamation-triangle [--p-icon-size:3rem] text-(--stela-color-warning) mb-4 block"></i>
                                    <p class="text-gray-600 mb-6">{{ 'set_password.invalid_token_help' | translate }}</p>
                                    <p-button styleClass="w-full" [label]="'set_password.back_to_login' | translate"
                                        (onClick)="goToLogin()" />
                                </div>
                            }
                            @case ('form') {
                                <a href="javascript:void(0)" (click)="goToLogin()"
                                    class="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-(--stela-gold) transition-colors mb-6">
                                    <i class="fa fa-arrow-left"></i>
                                    <span>{{ 'set_password.back_to_login' | translate }}</span>
                                </a>

                                <h5 class="text-2xl font-bold text-gray-800 mb-6">
                                    {{ (isActivation() ? 'set_password.title_activation' : purpose() === 'ResetPassword' ? 'set_password.title_reset' : 'set_password.title_set') | translate }}
                                </h5>

                                <div class="space-y-5">
                                    <div>
                                        <label class="block text-sm font-semibold text-gray-700 mb-2" for="newPassword">
                                            {{ 'set_password.new_password' | translate }}
                                        </label>
                                        <p-password inputId="newPassword" name="newPassword"
                                            [ngModel]="newPassword()" (ngModelChange)="newPassword.set($event)"
                                            [feedback]="true" [toggleMask]="true" [strongRegex]="strongRegex"
                                            autocomplete="new-password" [maxlength]="100"
                                            class="w-full block" styleClass="w-full" inputStyleClass="w-full" />
                                        <small class="block mt-1 text-xs text-gray-500">
                                            {{ 'set_password.password_requirements' | translate }}
                                        </small>
                                    </div>

                                    <div>
                                        <label class="block text-sm font-semibold text-gray-700 mb-2" for="confirmPassword">
                                            {{ 'set_password.confirm_password' | translate }}
                                        </label>
                                        <p-password inputId="confirmPassword" name="confirmPassword"
                                            [ngModel]="confirmPassword()" (ngModelChange)="confirmPassword.set($event)"
                                            [feedback]="false" [toggleMask]="true"
                                            autocomplete="new-password" [maxlength]="100"
                                            class="w-full block" styleClass="w-full" inputStyleClass="w-full" />
                                        @if (passwordsMismatch()) {
                                            <small class="block mt-1 text-xs text-(--stela-color-danger)">
                                                {{ 'set_password.passwords_mismatch' | translate }}
                                            </small>
                                        }
                                    </div>

                                    @if (errorMessage()) {
                                        <p-message severity="error" [text]="errorMessage()!" styleClass="w-full" />
                                    }

                                    <p-button styleClass="w-full" [label]="'set_password.submit' | translate"
                                        [disabled]="!canSubmit()" [loading]="submitting()" (onClick)="submit()" />
                                </div>
                            }
                            @case ('success') {
                                <div class="text-center py-4">
                                    <i class="pi pi-check-circle [--p-icon-size:3rem] text-(--stela-color-success) mb-4 block"></i>
                                    @if (authenticatorToken()) {
                                        <p class="font-semibold text-gray-800 mb-6">{{ 'set_password.success_activation_authenticator' | translate }}</p>
                                        <p-button styleClass="w-full" [label]="'set_password.go_to_authenticator' | translate"
                                            (onClick)="continueAfterSuccess()" />
                                    } @else if (isActivation()) {
                                        <p class="font-semibold text-gray-800 mb-6">{{ 'set_password.success_activation' | translate }}</p>
                                        <p-button styleClass="w-full" [label]="'set_password.go_to_login' | translate"
                                            (onClick)="continueAfterSuccess()" />
                                    } @else {
                                        <p class="font-semibold text-gray-800 mb-1">{{ 'set_password.success' | translate }}</p>
                                        <p class="text-sm text-gray-500 mb-6">{{ 'set_password.redirecting' | translate }}</p>
                                        <p-button styleClass="w-full" [label]="'set_password.go_to_login' | translate"
                                            (onClick)="continueAfterSuccess()" />
                                    }
                                </div>
                            }
                        }
                    </div>
                </div>
            </div>
        </div>
    `
})
export class SetPasswordComponent implements OnInit {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly setPasswordService = inject(SetPasswordService);
    private readonly log = inject(LogService);
    private readonly translate = inject(TranslateService);

    // Min. 8 chars, upper+lower case, digit and special char — matches the
    // 'set_password.password_requirements' hint shown under the field.
    readonly strongRegex = '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[^A-Za-z0-9]).{8,}$';

    readonly step = signal<Step>('loading');
    readonly purpose = signal<string | null>(null);
    readonly authenticatorToken = signal<string | null>(null);
    readonly isActivation = computed(() =>
        this.purpose() === 'Activation' || this.purpose() === 'ActivationWithAuthenticator');
    readonly newPassword = signal('');
    readonly confirmPassword = signal('');
    readonly submitting = signal(false);
    readonly errorMessage = signal<string | null>(null);

    readonly passwordsMismatch = computed(() =>
        this.confirmPassword().length > 0 && this.newPassword() !== this.confirmPassword());

    readonly canSubmit = computed(() =>
        this.newPassword().length > 0
        && this.confirmPassword().length > 0
        && this.newPassword() === this.confirmPassword()
        && !this.submitting());

    private token = '';

    ngOnInit(): void {
        this.token = this.route.snapshot.queryParams['token'] || '';
        if (!this.token) {
            this.step.set('invalid');
            return;
        }

        this.setPasswordService.validateToken(this.token).subscribe({
            next: (response) => {
                if (response.valid) {
                    this.purpose.set(response.purpose);
                    this.step.set('form');
                } else {
                    this.step.set('invalid');
                }
            },
            error: (err) => {
                this.log.error('Error validating reset token', err);
                this.step.set('invalid');
            }
        });
    }

    submit(): void {
        if (!this.canSubmit()) {
            return;
        }

        this.submitting.set(true);
        this.errorMessage.set(null);

        this.setPasswordService.resetPassword(this.token, this.newPassword()).subscribe({
            next: (response) => {
                this.submitting.set(false);
                this.authenticatorToken.set(response?.authenticatorToken ?? null);
                this.step.set('success');
            },
            error: (err) => {
                this.submitting.set(false);
                // El errorInterceptor entrega un ApiError: el cuerpo del 400 está en `details`.
                const code = err?.details ?? err?.error;
                this.errorMessage.set(
                    code === 'INVALID_OR_EXPIRED_TOKEN'
                        ? this.translate.instant('set_password.invalid_token_help')
                        : code === 'HISTORY_VIOLATION'
                            ? this.translate.instant('set_password.password_reused')
                            : (typeof code === 'string' ? code : this.translate.instant('set_password.generic_error')));
            }
        });
    }

    goToLogin(): void {
        this.router.navigate(['/auth/init']);
    }

    /** El destino coincide con el texto de la tarjeta: registro de Authenticator o login. */
    continueAfterSuccess(): void {
        const authenticatorToken = this.authenticatorToken();
        if (authenticatorToken) {
            this.router.navigate(['/auth/register/authenticator', authenticatorToken]);
            return;
        }
        this.goToLogin();
    }
}
